import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
} from '@mui/material';
import apiService from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';
import RowActionsMenu from './shared/RowActionsMenu';
import ResponsiveTableContainer from './shared/ResponsiveTableContainer';

const STATUS_CONFIG = {
  PENDING: { label: 'Függőben', color: 'warning' },
  REVIEWED: { label: 'Átnézve', color: 'info' },
  RESOLVED: { label: 'Megoldva', color: 'success' },
};

export default function BugReportsPage() {
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      const data = await apiService.get(
        `${API_ENDPOINTS.BUG_REPORTS.ADMIN_LIST}?${new URLSearchParams(params).toString()}`
      );
      setReports(data.reports || []);
      setPagination(data.pagination || { page: 1, totalPages: 1 });
    } catch {
      setError('Nem sikerült betölteni a hibabejelentéseket.');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleStatusChange = async (id, status) => {
    setActionLoading(id);
    try {
      await apiService.put(API_ENDPOINTS.BUG_REPORTS.ADMIN_UPDATE(id), { status });
      setReports((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status } : r))
      );
    } catch {
      setError('Státusz frissítése sikertelen.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Státusz szűrő</InputLabel>
          <Select
            value={statusFilter}
            label="Státusz szűrő"
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <MenuItem value="">Összes</MenuItem>
            <MenuItem value="PENDING">Függőben</MenuItem>
            <MenuItem value="REVIEWED">Átnézve</MenuItem>
            <MenuItem value="RESOLVED">Megoldva</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          Összesen: {pagination.total || 0} bejelentés
        </Typography>
      </Box>

      <ResponsiveTableContainer stickyLastColumn>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Dátum</TableCell>
              <TableCell>Oldal</TableCell>
              <TableCell sx={{ maxWidth: 300 }}>Leírás</TableCell>
              <TableCell>Felhasználó</TableCell>
              <TableCell>Státusz</TableCell>
              <TableCell>Műveletek</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Nincs hibabejelentés.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              reports.map((report) => {
                const cfg = STATUS_CONFIG[report.status] || STATUS_CONFIG.PENDING;
                return (
                  <TableRow key={report.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(report.createdAt)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {report.pageUrl || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Tooltip title={report.description} placement="top">
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {report.description}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {report.user ? (
                        <Typography variant="body2">{report.user.firstName} {report.user.lastName}</Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Vendég</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={cfg.label} color={cfg.color} size="small" />
                    </TableCell>
                    <TableCell>
                      <RowActionsMenu
                        primaryAction={
                          report.status !== 'RESOLVED'
                            ? {
                                label: 'Megoldva',
                                variant: 'contained',
                                color: 'success',
                                disabled: actionLoading === report.id,
                                onClick: () => handleStatusChange(report.id, 'RESOLVED'),
                              }
                            : null
                        }
                        menuActions={[
                          report.status !== 'REVIEWED' && {
                            label: 'Átnézve',
                            disabled: actionLoading === report.id,
                            onClick: () => handleStatusChange(report.id, 'REVIEWED'),
                          },
                          report.status !== 'PENDING' && {
                            label: 'Vissza',
                            color: 'warning',
                            disabled: actionLoading === report.id,
                            onClick: () => handleStatusChange(report.id, 'PENDING'),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {pagination.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              color="primary"
            />
          </Box>
        )}
      </ResponsiveTableContainer>
    </Box>
  );
}
